// Pillar page configs — 3 topical pillars that concentrate PageRank on
// /aula-gratis via curated internal links. Each pillar :
//   - has a rich hero + TL;DR
//   - lists sections with links to relevant blog posts + programmatic pages
//   - emits BreadcrumbList + Article + FAQPage JSON-LD
//   - drives every reader to /aula-gratis
//
// Add more pillars here → App.js loops over PILLAR_KEYS and registers routes.

export const PILLARS = {
  "frances-do-zero": {
    slug: "frances-do-zero",
    updated: "2026-02-24",
    color: "#1e2a5a",
    hero: {
      pt: {
        eyebrow: "Guia-pilar · nível A0-A1",
        title: "Francês do zero em 90 dias",
        subtitle:
          "Um plano completo com áudios, gramática essencial, vocabulário mínimo e primeiras frases pra falar sem congelar.",
      },
      en: {
        eyebrow: "Pillar guide · level A0-A1",
        title: "French from scratch in 90 days",
        subtitle:
          "Complete plan with audios, essential grammar, minimum vocab and first phrases to speak without freezing.",
      },
      fr: {
        eyebrow: "Guide pilier · niveau A0-A1",
        title: "Le français en partant de zéro, en 90 jours",
        subtitle:
          "Un plan complet avec audios, grammaire essentielle, vocabulaire minimum et premières phrases.",
      },
    },
    tldr: {
      pt: "Pra sair do zero em francês, foque em 3 blocos por 90 dias: (1) pronúncia do R uvular + vogais nasais, (2) 300 palavras de vocabulário base, (3) 4 tempos verbais (présent, passé composé, futur proche, imparfait). Dedique 20-30 min/dia. Guia completo abaixo, com links pros posts detalhados.",
      en: "To go from zero in French, focus on 3 blocks over 90 days: (1) uvular R + nasal vowels, (2) 300 base vocab words, (3) 4 verb tenses (présent, passé composé, futur proche, imparfait). 20-30 min/day. Full guide below with links to detailed posts.",
      fr: "Pour partir de zéro, concentre 3 blocs sur 90 jours : (1) R uvulaire + voyelles nasales, (2) 300 mots de vocabulaire de base, (3) 4 temps verbaux (présent, passé composé, futur proche, imparfait). 20-30 min/jour. Guide complet ci-dessous.",
    },
    sections: {
      pt: [
        {
          title: "Semana 1-3 — Pronúncia sem sotaque de gringo",
          body: "Antes de qualquer gramática, seus 21 primeiros dias vão pro som. Sem isso, você aprende palavras que ninguém entende.",
          links: [
            { to: "/blog/como-pronunciar-r-frances-21-dias", label: "R francês em 21 dias — método completo" },
            { to: "/frances/pronuncia/bonjour", label: "Como pronunciar « Bonjour »" },
            { to: "/frances/pronuncia/paris", label: "Como pronunciar « Paris »" },
            { to: "/frances/pronuncia/oui", label: "Como pronunciar « Oui »" },
          ],
        },
        {
          title: "Semana 4-8 — 300 palavras de base + primeiras frases",
          body: "Vocabulário mínimo pra sobreviver: cumprimentos, números, comida, transporte, emergências. Foco em USO, não em listas decoradas.",
          links: [
            { to: "/blog/numeros-em-frances-guia-com-audio", label: "Números em francês, com áudio" },
            { to: "/frances/como-dizer/obrigado", label: "Como dizer « obrigado »" },
            { to: "/frances/como-dizer/bom-dia", label: "Como dizer « bom dia »" },
            { to: "/frances/como-dizer/quanto-custa", label: "Como perguntar « quanto custa »" },
            { to: "/frances/como-dizer/onde-fica", label: "Como perguntar « onde fica »" },
          ],
        },
        {
          title: "Semana 9-12 — Gramática essencial (só o que importa)",
          body: "Você NÃO precisa saber os 8 tempos verbais franceses. 4 já cobrem 95% da conversa real: présent, passé composé, imparfait, futur proche.",
          links: [
            { to: "/blog/passe-compose-desmistificado", label: "Passé composé desmistificado" },
            { to: "/frances/diferenca/tu-vs-vous", label: "TU vs VOUS — o erro social nº 1" },
            { to: "/frances/diferenca/savoir-vs-connaitre", label: "Savoir vs Connaître" },
            { to: "/frances/diferenca/bien-vs-bon", label: "Bien vs Bon — adjetivo ou advérbio?" },
          ],
        },
      ],
      en: [
        {
          title: "Week 1-3 — Pronunciation without a gringo accent",
          body: "Before any grammar, your first 21 days go to sound. Without it, you learn words nobody understands.",
          links: [
            { to: "/blog/como-pronunciar-r-frances-21-dias", label: "French R in 21 days" },
            { to: "/frances/pronuncia/bonjour", label: "How to pronounce Bonjour" },
            { to: "/frances/pronuncia/paris", label: "How to pronounce Paris" },
            { to: "/frances/pronuncia/oui", label: "How to pronounce Oui" },
          ],
        },
        {
          title: "Week 4-8 — 300 base words + first phrases",
          body: "Survival vocab: greetings, numbers, food, transport, emergencies. Focus on USE, not memorised lists.",
          links: [
            { to: "/blog/numeros-em-frances-guia-com-audio", label: "French numbers with audio" },
            { to: "/frances/como-dizer/obrigado", label: "How to say thank you" },
            { to: "/frances/como-dizer/bom-dia", label: "How to say hello" },
            { to: "/frances/como-dizer/quanto-custa", label: "How to ask how much" },
            { to: "/frances/como-dizer/onde-fica", label: "How to ask where is" },
          ],
        },
        {
          title: "Week 9-12 — Essential grammar (only what matters)",
          body: "You do NOT need all 8 French tenses. 4 cover 95 % of real conversation: présent, passé composé, imparfait, futur proche.",
          links: [
            { to: "/blog/passe-compose-desmistificado", label: "Passé composé demystified" },
            { to: "/frances/diferenca/tu-vs-vous", label: "TU vs VOUS — social mistake #1" },
            { to: "/frances/diferenca/savoir-vs-connaitre", label: "Savoir vs Connaître" },
            { to: "/frances/diferenca/bien-vs-bon", label: "Bien vs Bon — adjective or adverb?" },
          ],
        },
      ],
      fr: [
        {
          title: "Semaine 1-3 — Prononciation propre",
          body: "Avant toute grammaire, tes 21 premiers jours vont au son. Sans ça, tu apprends des mots que personne ne comprend.",
          links: [
            { to: "/blog/como-pronunciar-r-frances-21-dias", label: "Le R en 21 jours" },
            { to: "/frances/pronuncia/bonjour", label: "Prononcer Bonjour" },
            { to: "/frances/pronuncia/paris", label: "Prononcer Paris" },
          ],
        },
        {
          title: "Semaine 4-8 — 300 mots de base",
          body: "Vocabulaire de survie : salutations, chiffres, nourriture, transport, urgences.",
          links: [
            { to: "/blog/numeros-em-frances-guia-com-audio", label: "Les nombres avec audio" },
            { to: "/frances/como-dizer/obrigado", label: "Dire merci" },
            { to: "/frances/como-dizer/bom-dia", label: "Dire bonjour" },
          ],
        },
        {
          title: "Semaine 9-12 — Grammaire essentielle",
          body: "4 temps suffisent pour 95 % des conversations.",
          links: [
            { to: "/blog/passe-compose-desmistificado", label: "Le passé composé" },
            { to: "/frances/diferenca/tu-vs-vous", label: "TU vs VOUS" },
            { to: "/frances/diferenca/savoir-vs-connaitre", label: "Savoir vs Connaître" },
          ],
        },
      ],
    },
    faq: {
      pt: [
        { q: "Dá pra aprender francês do zero em 90 dias?", a: "Não pra fluência, mas SIM pra chegar em A2/B1 conversacional: fazer compras, pedir comida, se apresentar, contar sua rotina. Fluência real leva 1-2 anos de imersão consistente." },
        { q: "Preciso morar na França pra aprender?", a: "Não. 80% dos meus alunos brasileiros nunca pisaram lá. O segredo é constância + input diário (podcasts, séries, música) + fala regular com nativo mesmo online." },
        { q: "Qual o pior erro do iniciante?", a: "Estudar gramática antes de treinar pronúncia. Você vira um « francês em texto » que nenhum nativo entende. Inverta: 21 dias de som PRIMEIRO, aí gramática." },
      ],
      en: [
        { q: "Can I learn French from zero in 90 days?", a: "Not fluent, but YES to reach A2/B1 conversational: shop, order food, introduce yourself, describe your routine. Real fluency takes 1-2 years of consistent immersion." },
        { q: "Do I need to live in France to learn?", a: "No. 80 % of my Brazilian students never set foot there. Secret: consistency + daily input (podcasts, shows, music) + regular native speaking, even online." },
        { q: "Biggest beginner mistake?", a: "Studying grammar before drilling pronunciation. You become a « French on paper » nobody understands. Flip it: 21 days of sound FIRST, then grammar." },
      ],
      fr: [
        { q: "Peut-on apprendre le français de zéro en 90 jours ?", a: "Pas la fluence, mais oui A2/B1 conversationnel : faire des courses, commander, se présenter." },
        { q: "Faut-il vivre en France ?", a: "Non. 80 % de mes élèves n'y ont jamais mis les pieds. La clé : régularité + input quotidien + parler avec un natif même en ligne." },
        { q: "Erreur n° 1 du débutant ?", a: "Étudier la grammaire avant la prononciation. Inverse : 21 jours de son d'abord." },
      ],
    },
  },

  "frances-para-viagem": {
    slug: "frances-para-viagem",
    updated: "2026-02-24",
    color: "#d9381e",
    hero: {
      pt: {
        eyebrow: "Guia-pilar · viagem França",
        title: "Francês pra viagem em 15 dias",
        subtitle:
          "60 frases essenciais, pronúncia dos lugares turísticos, etiqueta francesa e sobrevivência real em Paris + interior.",
      },
      en: {
        eyebrow: "Pillar guide · France travel",
        title: "Travel French in 15 days",
        subtitle:
          "60 essential phrases, tourist-spot pronunciation, French etiquette and real survival in Paris + countryside.",
      },
      fr: {
        eyebrow: "Guide pilier · voyage France",
        title: "Français voyage en 15 jours",
        subtitle:
          "60 phrases essentielles, prononciation des lieux touristiques, étiquette et survie à Paris + campagne.",
      },
    },
    tldr: {
      pt: "Pra sobreviver 15 dias na França sem inglês: (1) decore 60 frases divididas em 6 categorias (aeroporto, transporte, hotel, restaurante, compras, emergências), (2) domine « Bonjour » — muda 80% da recepção, (3) pratique 5 pronúncias-chave (bonjour, merci, pardon, oui, croissant). Roteiro completo abaixo.",
      en: "To survive 15 days in France without English: (1) memorise 60 phrases in 6 categories (airport, transport, hotel, restaurant, shopping, emergencies), (2) master « Bonjour » — it changes 80 % of the reception, (3) drill 5 key pronunciations. Full playbook below.",
      fr: "Pour survivre 15 jours sans anglais : (1) 60 phrases en 6 catégories, (2) maîtrise « Bonjour », (3) 5 prononciations clés. Feuille de route complète.",
    },
    sections: {
      pt: [
        {
          title: "Etiqueta francesa — a regra invisível",
          body: "Iniciar TODA interação com « Bonjour ! » (com sorriso). Isso desarma 90% do estereótipo do « francês mal-educado ». Fechar toda saída com « Merci, bonne journée ! ».",
          links: [
            { to: "/frances/como-dizer/bom-dia", label: "Como dizer bom dia · variações" },
            { to: "/frances/como-dizer/tchau", label: "Como se despedir educadamente" },
            { to: "/frances/como-dizer/desculpa", label: "Pardon vs Excusez-moi" },
          ],
        },
        {
          title: "Aeroporto Charles de Gaulle — as 10 frases",
          body: "Da imigração à saída de metrô com bagagem: o roteiro exato, com áudio da minha voz.",
          links: [
            { to: "/paris-em-10-frases", label: "Landing page com áudio · 10 frases CDG" },
            { to: "/blog/10-frases-aeroporto-charles-de-gaulle", label: "Post completo · aeroporto CDG" },
          ],
        },
        {
          title: "Restaurante — pedir, entender, pagar",
          body: "A conta NUNCA vem espontaneamente. Gorjeta NÃO é obrigatória. Nunca chame o garçom de « garçon ! ».",
          links: [
            { to: "/frances/como-dizer/a-conta-por-favor", label: "Como pedir a conta" },
            { to: "/blog/como-pedir-restaurante-frances-sem-vergonha", label: "Post completo · restaurante sem vergonha" },
          ],
        },
        {
          title: "Compras e preços",
          body: "Como perguntar preço, pedir pra escrever, e o que dizer no caixa.",
          links: [
            { to: "/frances/como-dizer/quanto-custa", label: "Como perguntar « quanto custa »" },
            { to: "/blog/vocabulario-viagem-franca-15-dias", label: "Vocabulário completo · 15 dias" },
          ],
        },
        {
          title: "Emergência e ajuda",
          body: "Perdi o caminho? Perdi o passaporte? A frase salva-vidas + 3 palavras que abrem qualquer porta.",
          links: [
            { to: "/frances/como-dizer/estou-perdido", label: "Como dizer « estou perdido »" },
            { to: "/frances/como-dizer/onde-fica", label: "Como perguntar « onde fica »" },
            { to: "/frances/como-dizer/voce-fala-portugues", label: "Como perguntar se fala português" },
          ],
        },
        {
          title: "Pronúncia dos essenciais",
          body: "Antes de embarcar, treine 5 palavras. Nada garante mais que o francês te entenda no primeiro try.",
          links: [
            { to: "/frances/pronuncia/bonjour", label: "Bonjour · IPA e método" },
            { to: "/frances/pronuncia/croissant", label: "Croissant · sem T final!" },
            { to: "/frances/pronuncia/paris", label: "Paris · sem S!" },
            { to: "/frances/pronuncia/oui", label: "Oui · uma sílaba" },
            { to: "/frances/pronuncia/rendez-vous", label: "Rendez-vous" },
          ],
        },
      ],
      en: [
        {
          title: "French etiquette — the invisible rule",
          body: "Start EVERY interaction with « Bonjour ! » (with a smile). This disarms 90 % of the « rude French » stereotype. Always close with « Merci, bonne journée ! ».",
          links: [
            { to: "/frances/como-dizer/bom-dia", label: "How to say hello · variations" },
            { to: "/frances/como-dizer/tchau", label: "How to say goodbye politely" },
            { to: "/frances/como-dizer/desculpa", label: "Pardon vs Excusez-moi" },
          ],
        },
        {
          title: "Charles de Gaulle airport — the 10 phrases",
          body: "From immigration to metro exit with luggage: exact script with audio.",
          links: [
            { to: "/paris-em-10-frases", label: "Landing page with audio · 10 CDG phrases" },
            { to: "/blog/10-frases-aeroporto-charles-de-gaulle", label: "Full post · CDG airport" },
          ],
        },
        {
          title: "Restaurant — order, understand, pay",
          body: "The bill NEVER comes on its own. Tipping NOT mandatory. Never call the waiter « garçon ! ».",
          links: [
            { to: "/frances/como-dizer/a-conta-por-favor", label: "How to ask for the bill" },
            { to: "/blog/como-pedir-restaurante-frances-sem-vergonha", label: "Full restaurant playbook" },
          ],
        },
        {
          title: "Shopping and prices",
          body: "How to ask a price, get them to write it down, and what to say at checkout.",
          links: [
            { to: "/frances/como-dizer/quanto-custa", label: "How to ask how much" },
            { to: "/blog/vocabulario-viagem-franca-15-dias", label: "Full 15-day travel vocab" },
          ],
        },
        {
          title: "Emergencies and help",
          body: "Lost the way? Lost the passport? Life-saving phrase + 3 words that open any door.",
          links: [
            { to: "/frances/como-dizer/estou-perdido", label: "How to say I'm lost" },
            { to: "/frances/como-dizer/voce-fala-portugues", label: "How to ask if they speak Portuguese" },
          ],
        },
      ],
      fr: [
        {
          title: "Étiquette française — la règle invisible",
          body: "Commencer TOUTE interaction par « Bonjour ! ». Fermer par « Merci, bonne journée ! ».",
          links: [
            { to: "/frances/como-dizer/bom-dia", label: "Dire bonjour · variantes" },
            { to: "/frances/como-dizer/desculpa", label: "Pardon vs Excusez-moi" },
          ],
        },
        {
          title: "Aéroport CDG — les 10 phrases",
          body: "De l'immigration à la sortie métro avec bagages.",
          links: [
            { to: "/paris-em-10-frases", label: "Landing page audio · 10 phrases CDG" },
          ],
        },
        {
          title: "Restaurant — commander, payer",
          body: "L'addition ne vient jamais spontanément. Pourboire non obligatoire.",
          links: [
            { to: "/frances/como-dizer/a-conta-por-favor", label: "Demander l'addition" },
          ],
        },
      ],
    },
    faq: {
      pt: [
        { q: "Preciso mesmo saber francês pra visitar Paris?", a: "Não obrigatoriamente, mas muda tudo. Fora do centro turístico, muitos franceses não falam inglês. E mesmo os que falam respondem MUITO melhor se você começa por « Bonjour, parlez-vous anglais ? »." },
        { q: "Quantos dias antes começar a estudar?", a: "Mínimo 15 dias intensivos (30-45 min/dia). Ideal: 30 dias. Foco: 60 frases + 5 pronúncias-chave. Não tente aprender gramática — cole nas frases prontas." },
        { q: "Qual é o app mais útil pra viagem?", a: "Google Translate offline (baixe o pacote francês antes) + DeepL (traduções melhores). Pra pronúncia, Forvo. Mas nada substitui treinar as 10 frases de aeroporto AO VIVO antes." },
      ],
      en: [
        { q: "Do I really need French to visit Paris?", a: "Not strictly, but it changes everything. Outside the tourist core, many French don't speak English. Even those who do respond MUCH better if you start with « Bonjour, parlez-vous anglais ? »." },
        { q: "How many days before the trip should I start?", a: "Minimum 15 intensive days (30-45 min/day). Ideal: 30 days. Focus: 60 phrases + 5 key pronunciations." },
        { q: "Best app for travel?", a: "Google Translate offline (download French pack before) + DeepL (better translations). For pronunciation, Forvo." },
      ],
      fr: [
        { q: "Faut-il vraiment parler français pour Paris ?", a: "Pas obligatoire, mais ça change tout." },
        { q: "Combien de jours pour préparer ?", a: "15 jours minimum, 30 jours idéal." },
        { q: "Meilleure app pour voyager ?", a: "Google Translate offline + DeepL + Forvo pour la prononciation." },
      ],
    },
  },

  "sotaque-parisiense": {
    slug: "sotaque-parisiense",
    updated: "2026-02-24",
    color: "#c99a4b",
    hero: {
      pt: {
        eyebrow: "Guia-pilar · pronúncia parisiense",
        title: "Sotaque parisiense pra brasileiros",
        subtitle:
          "Método muscular pra pronunciar como um nativo de Paris: R uvular, vogais nasais, consoantes mudas e ritmo silábico.",
      },
      en: {
        eyebrow: "Pillar guide · Parisian accent",
        title: "Parisian accent for Brazilians",
        subtitle:
          "Muscular method to sound like a native Parisian: uvular R, nasal vowels, silent consonants and syllabic rhythm.",
      },
      fr: {
        eyebrow: "Guide pilier · accent parisien",
        title: "L'accent parisien pour Brésiliens",
        subtitle:
          "Méthode musculaire pour sonner Parisien : R uvulaire, voyelles nasales, consonnes muettes et rythme.",
      },
    },
    tldr: {
      pt: "O sotaque parisiense se apoia em 4 pilares muscares: (1) R uvular no fundo da garganta, (2) 4 vogais nasais (an, in, on, un), (3) consoantes finais MUDAS (T, D, S, P, X), (4) ritmo silábico plano — sem tônica forte tipo português. 5 min/dia por 3 meses = sotaque credível.",
      en: "The Parisian accent rests on 4 muscular pillars: (1) uvular R deep in the throat, (2) 4 nasal vowels (an, in, on, un), (3) SILENT final consonants (T, D, S, P, X), (4) flat syllabic rhythm — no strong stress like English/Portuguese. 5 min/day for 3 months = credible accent.",
      fr: "L'accent parisien repose sur 4 piliers : R uvulaire, 4 voyelles nasales, consonnes finales muettes, rythme syllabique plat. 5 min/jour, 3 mois.",
    },
    sections: {
      pt: [
        {
          title: "Pilar 1 — O R uvular",
          body: "O R francês nasce no fundo da garganta, contra a úvula. Nada de « carro » brasileiro. É o pilar mais visível: um R errado « denuncia » o brasileiro em 1 segundo.",
          links: [
            { to: "/blog/como-pronunciar-r-frances-21-dias", label: "R francês em 21 dias · método completo" },
            { to: "/frances/pronuncia/paris", label: "Paris · praticar R uvular" },
            { to: "/frances/pronuncia/rendez-vous", label: "Rendez-vous · R + nasal" },
          ],
        },
        {
          title: "Pilar 2 — As 4 vogais nasais",
          body: "AN (/ɑ̃/), IN (/ɛ̃/), ON (/ɔ̃/), UN (/œ̃/). O ar sai pelo NARIZ e pela BOCA ao mesmo tempo. Português brasileiro tem nasais, mas mais fechadas. Aprenda a diferença ou vira turista eterno.",
          links: [
            { to: "/frances/pronuncia/bonjour", label: "Bonjour · vogal ON" },
            { to: "/frances/pronuncia/croissant", label: "Croissant · vogal AN" },
          ],
        },
        {
          title: "Pilar 3 — Consoantes finais mudas",
          body: "Regra CaReFuL: só C, R, F, L costumam soar no fim. T, D, S, P, X quase sempre mudas. Pronunciar consoantes finais é o marcador nº 2 de sotaque brasileiro.",
          links: [
            { to: "/frances/pronuncia/paris", label: "Paris · S final mudo" },
            { to: "/frances/pronuncia/croissant", label: "Croissant · T final mudo" },
            { to: "/frances/pronuncia/je-ne-sais-pas", label: "Je ne sais pas · S mudo" },
          ],
        },
        {
          title: "Pilar 4 — Ritmo silábico plano",
          body: "Português tem tônicas fortes (« ba-NA-na »). Francês parisiense tem sílabas quase iguais, com pequena ênfase na última. Falar francês com ritmo de português = sotaque na hora.",
          links: [
            { to: "/blog/20-girias-parisienses-2026", label: "20 gírias parisienses · como os locais falam" },
            { to: "/blog/frances-franca-vs-quebec", label: "Diferença de sotaque França vs Quebec" },
          ],
        },
      ],
      en: [
        {
          title: "Pillar 1 — The uvular R",
          body: "The French R is born at the back of the throat, against the uvula. Not the Brazilian « rolled R ». Most visible pillar — a wrong R « outs » a Brazilian in 1 second.",
          links: [
            { to: "/blog/como-pronunciar-r-frances-21-dias", label: "French R in 21 days" },
            { to: "/frances/pronuncia/paris", label: "Paris · drill uvular R" },
          ],
        },
        {
          title: "Pillar 2 — The 4 nasal vowels",
          body: "AN, IN, ON, UN. Air exits through the NOSE and MOUTH simultaneously. English has no nasal vowels — you have to build the muscle from scratch.",
          links: [
            { to: "/frances/pronuncia/bonjour", label: "Bonjour · nasal ON" },
            { to: "/frances/pronuncia/croissant", label: "Croissant · nasal AN" },
          ],
        },
        {
          title: "Pillar 3 — Silent final consonants",
          body: "CaReFuL rule: only C, R, F, L tend to sound at the end. T, D, S, P, X almost always silent.",
          links: [
            { to: "/frances/pronuncia/paris", label: "Paris · silent S" },
            { to: "/frances/pronuncia/croissant", label: "Croissant · silent T" },
          ],
        },
        {
          title: "Pillar 4 — Flat syllabic rhythm",
          body: "English/Portuguese have strong stress. Parisian French has nearly equal syllables, with tiny emphasis on the last.",
          links: [
            { to: "/blog/20-girias-parisienses-2026", label: "20 Parisian slangs" },
            { to: "/blog/frances-franca-vs-quebec", label: "France vs Quebec accent" },
          ],
        },
      ],
      fr: [
        {
          title: "Pilier 1 — Le R uvulaire",
          body: "Naît au fond de la gorge, contre la luette.",
          links: [{ to: "/blog/como-pronunciar-r-frances-21-dias", label: "Le R en 21 jours" }],
        },
        {
          title: "Pilier 2 — Les 4 voyelles nasales",
          body: "AN, IN, ON, UN. L'air sort par le nez et la bouche en même temps.",
          links: [
            { to: "/frances/pronuncia/bonjour", label: "Bonjour · nasale ON" },
            { to: "/frances/pronuncia/croissant", label: "Croissant · nasale AN" },
          ],
        },
        {
          title: "Pilier 3 — Consonnes finales muettes",
          body: "Règle CaReFuL : seuls C, R, F, L tendent à sonner en fin.",
          links: [{ to: "/frances/pronuncia/paris", label: "Paris · S muet" }],
        },
        {
          title: "Pilier 4 — Rythme syllabique plat",
          body: "Syllabes presque égales, petite emphase sur la dernière.",
          links: [{ to: "/blog/20-girias-parisienses-2026", label: "20 argots parisiens" }],
        },
      ],
    },
    faq: {
      pt: [
        { q: "Quanto tempo pra ter um sotaque parisiense credível?", a: "Com 5-10 min/dia focados nos 4 pilares (R uvular, nasais, consoantes mudas, ritmo), a maioria dos meus alunos chega em 3 meses. Sotaque « invisível » = 1-2 anos. Mas passar de « brasileiro assumido » pra « estrangeiro discreto » = 3 meses." },
        { q: "Existe diferença entre sotaque parisiense e « francês padrão »?", a: "Sim, mas para o brasileiro iniciante, é a mesma coisa. O sotaque parisiense É o padrão da mídia francesa, TV5, novelas, dublagem. Existe um sotaque parisiense POPULAR (com R mais forte, gírias) mas isso é nuance." },
        { q: "Sotaque parisiense ou sotaque quebequense?", a: "Depende do seu objetivo. Vai pra França/Europa: parisiense. Vai pro Canadá/Quebec/imigração: quebequense (é uma língua quase separada em termos de sotaque). Não misture os dois — os falantes reconhecem imediatamente." },
      ],
      en: [
        { q: "How long to have a credible Parisian accent?", a: "With 5-10 min/day on the 4 pillars, most students reach it in 3 months. « Invisible » accent = 1-2 years. Going from « obvious Brazilian » to « discreet foreigner » = 3 months." },
        { q: "Is there a difference between Parisian and « standard French »?", a: "Yes, but for beginners, it's the same. Parisian IS the media standard (TV5, dubbing)." },
        { q: "Parisian or Quebec accent?", a: "Depends on your goal. France/Europe: Parisian. Canada/Quebec/immigration: Quebecois (almost a separate accent). Never mix them." },
      ],
      fr: [
        { q: "Combien de temps pour un accent parisien crédible ?", a: "3 mois avec 5-10 min/jour sur les 4 piliers." },
        { q: "Accent parisien vs français standard ?", a: "Le parisien est le standard des médias." },
        { q: "Parisien ou québécois ?", a: "Selon ton objectif. Ne mélange jamais." },
      ],
    },
  },
};

export const PILLAR_KEYS = Object.keys(PILLARS);

export function getPillar(slug) {
  return PILLARS[slug] || null;
}
